import * as RadixCheckbox from '@radix-ui/react-checkbox';
import { type ComponentPropsWithoutRef, type ReactNode, useState } from 'react';
import { tv } from 'tailwind-variants';

import Dropdown from '../atoms/Dropdown';
import Text from '../base/Text';
import CheckedIcon from '../icons/CheckedIcon';
import ChevronDownIcon from '../icons/ChevronDownIcon';
import UncheckedIcon from '../icons/UncheckedIcon';

const checkboxDropdownButton = tv({
  base: 'border-primary bg-primary text-primary dark:border-gray-75 dark:bg-gray-90 relative inline-flex h-[30px] min-w-[30px] items-center gap-2 rounded-lg border py-[5px] pl-[5px] pr-3 transition-all duration-200 ease-in-out hover:no-underline',
  variants: {
    dropdownOpen: {
      true: 'bg-secondary dark:bg-gray-75',
      false: '',
    },
    disabled: {
      true: 'bg-gray-30 text-gray-90 cursor-not-allowed opacity-50',
      false: '',
    },
  },
  defaultVariants: {
    dropdownOpen: false,
    disabled: false,
  },
});

const checkboxDropdownToggle = tv({
  base: 'focus-visible relative inline-flex min-w-0 flex-none items-center gap-1 transition-colors',
  variants: {
    disabled: {
      true: 'cursor-not-allowed opacity-50',
      false: 'cursor-pointer active:scale-[0.98]',
    },
  },
  defaultVariants: {
    disabled: false,
  },
});

const checkboxDropdownTrigger = tv({
  base: 'focus-visible flex size-5 flex-none items-center justify-center transition-colors',
  variants: {
    disabled: {
      true: 'cursor-not-allowed opacity-50',
      false: 'cursor-pointer active:scale-[0.98]',
    },
  },
  defaultVariants: {
    disabled: false,
  },
});

export interface CheckboxDropdownButtonProps
  extends Omit<
    ComponentPropsWithoutRef<typeof RadixCheckbox.Root>,
    'checked' | 'children' | 'onCheckedChange'
  > {
  checked: boolean;
  children?: ReactNode;
  dropdownAriaLabel: string;
  dropdownContent: ReactNode;
  dropdownDisabled?: boolean;
  dropdownWidth?: number;
  onCheckedChange?: (checked: boolean) => void;
}

export default function CheckboxDropdownButton({
  checked,
  children,
  className,
  disabled = false,
  dropdownAriaLabel,
  dropdownContent,
  dropdownDisabled,
  dropdownWidth,
  onCheckedChange,
  ...props
}: CheckboxDropdownButtonProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const resolvedDropdownDisabled = dropdownDisabled ?? disabled;

  return (
    <div
      className={checkboxDropdownButton({
        dropdownOpen,
        disabled: disabled && resolvedDropdownDisabled,
        className,
      })}
    >
      <RadixCheckbox.Root
        checked={checked}
        className={checkboxDropdownToggle({ disabled })}
        disabled={disabled}
        onCheckedChange={(nextChecked) =>
          onCheckedChange?.(nextChecked === true)
        }
        type="button"
        {...props}
      >
        <CheckboxDropdownButtonIndicator checked={checked} />
        {typeof children === 'string' ? (
          <Text variant="studio/body/medium/xs" className="truncate">
            {children}
          </Text>
        ) : (
          children
        )}
      </RadixCheckbox.Root>

      <Dropdown
        align="center"
        side="top"
        width={dropdownWidth}
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        contentVariant="panel"
        disabled={resolvedDropdownDisabled}
        trigger={
          <button
            type="button"
            aria-label={dropdownAriaLabel}
            className={checkboxDropdownTrigger({
              disabled: resolvedDropdownDisabled,
            })}
            disabled={resolvedDropdownDisabled}
          >
            <ChevronDownIcon
              className={`text-gray-45 dark:text-gray-30 h-4 w-4 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
            />
          </button>
        }
      >
        {dropdownContent}
      </Dropdown>
    </div>
  );
}

function CheckboxDropdownButtonIndicator({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden
      className="flex h-5 w-5 flex-none items-center justify-center"
    >
      {checked ? (
        <CheckedIcon className="text-blue h-5 w-5" />
      ) : (
        <UncheckedIcon className="text-gray-45 dark:text-gray-30 h-5 w-5" />
      )}
    </span>
  );
}
